import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { Reveal, Overline } from "./motion";
import { useTranslate } from "../context/TranslateContext";
import { ARTISTS } from "../mock/data";

const ROLES = [
  "Actors", "Directors", "Producers", "Writers", "Cinematographers", "Editors",
  "Composers", "Singers", "Lyricists", "Choreographers", "VFX Artists", "Critics",
];

export const People = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const listenText = "Behind every frame are people. Explore detailed profiles of actors, directors, composers, singers and the craftspeople of Indian cinema — their filmographies, collaborations, awards and stories.";

  return (
    <section id="people" className="relative border-y border-edge bg-surface/30 py-28 md:py-40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Overline>People</Overline>
            <h2 className="mt-4 font-display text-4xl font-semibold leading-tight tracking-tight text-cream md:text-6xl">
              {t("The ")}<span className="italic text-gradient-gold">{t("faces & hands")}</span>{t(" behind the frame")}
            </h2>
            <p className="mt-6 font-body text-lg leading-relaxed text-ash">
              {t("A living directory of every professional who shapes Indian cinema and music — verified profiles, complete credits and the web of collaborations that connects them all.")}
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {ROLES.map((r) => (
                <span key={r} className="rounded-full bg-surface px-3 py-1.5 font-body text-xs text-ash">{t(r)}</span>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              
              <Link to="/article/people-overview" data-testid="read-people-guide"
                className="inline-flex items-center gap-2 rounded-full border border-gold/50 px-5 py-2.5 font-body text-sm text-gold transition-colors hover:bg-gold/10">
                {t("Read: The People Behind the Screen")} <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6 lg:col-span-7">
            {ARTISTS.map((a, i) => (
              <Reveal key={a.name} delay={i * 0.08} className={i === 1 ? "mt-10" : ""}>
                <motion.div whileHover={{ y: -8 }} transition={{ duration: 0.4 }}
                  data-testid={`artist-card-${i}`} onClick={() => navigate(`/person/${a.id}`)}
                  className="group relative cursor-pointer overflow-hidden rounded-2xl border border-edge">
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img src={a.image} alt={a.name}
                      className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/10 to-transparent" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="font-display text-2xl font-semibold text-paper drop-shadow-lg md:text-3xl">{a.name}</h3>
                    <p className="font-body text-sm text-gold">{t(a.role)}</p>
                  </div>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
